/**
 * StepWizard — horizontal step progress indicator.
 *
 * Renders a row of step pills connected by thin lines.
 * Three visual states: completed (solid green), current (blue border),
 * and pending (gray border).
 *
 * Matches Figma node 12029-24169 "Action Steps".
 */

import React, { type ReactNode } from "react";
import { View } from "react-native";
import { AppText } from "@/components/ui/AppText";
import { useI18n } from "@/context/I18nContext";
import type { TranslationKeys } from "@chinese-vendor/i18n";

// ─── Types ────────────────────────────────────────────────────────────────────

export type StepStatus = "completed" | "current" | "pending";

export interface StepWizardProps {
  /** 1-based index of the currently active step. */
  step: number;
  /** Translation key for each step's label (in display order). */
  labels: TranslationKeys[];
  /**
   * Layout direction.
   * - `"horizontal"` (default) — pills in a row with connector lines; children render below.
   * - `"vertical"` — pills stacked in a column; children fill the space to the right.
   */
  orientation?: "horizontal" | "vertical";
  /**
   * Optional content to render alongside the step list.
   * Horizontal: renders below the pills row.
   * Vertical: renders to the right of the pills column.
   */
  children?: ReactNode;
  testID?: string;
}

// ─── Step item ────────────────────────────────────────────────────────────────

interface StepItemProps {
  label: TranslationKeys;
  stepNum: number;
  status: StepStatus;
}

function StepItem({ label, stepNum, status }: StepItemProps) {
  const { t } = useI18n();

  if (status === "completed") {
    return (
      <View
        testID={`step-item-${stepNum}-completed`}
        className="flex-row items-center gap-2 bg-success border-2 border-success rounded-full px-3 py-3"
      >
        {/* Checkmark circle */}
        <View
          className="w-6 h-6 rounded-full bg-success items-center justify-center shrink-0"
          accessibilityElementsHidden
        >
          <AppText weight="bold" className="text-white text-xs leading-none">
            ✓
          </AppText>
        </View>
        <AppText weight="semibold" className="text-base leading-tight text-white">
          {t(label)}
        </AppText>
      </View>
    );
  }

  if (status === "current") {
    return (
      <View
        testID={`step-item-${stepNum}-current`}
        className="flex-row items-center gap-2 border-2 border-blue-50 rounded-full px-3 py-3"
      >
        {/* Number circle */}
        <View className="w-6 h-6 rounded-full border-2 border-blue-50 bg-white items-center justify-center shrink-0">
          <AppText
            weight="semibold"
            className="text-sm text-dark-text-50 leading-tight tracking-wide"
          >
            {String(stepNum)}
          </AppText>
        </View>
        <AppText weight="medium" className="text-base text-dark-text-50">
          {t(label)}
        </AppText>
      </View>
    );
  }

  // pending
  return (
    <View
      testID={`step-item-${stepNum}-pending`}
      className="flex-row items-center gap-2 border-2 border-stroke-50 rounded-full px-3 py-3"
    >
      {/* Number circle — filled gray */}
      <View className="w-6 h-6 rounded-full bg-stroke-30 items-center justify-center shrink-0">
        <AppText
          weight="medium"
          className="text-sm text-supporting-text leading-tight tracking-wide"
        >
          {String(stepNum)}
        </AppText>
      </View>
      <AppText weight="medium" className="text-base text-supporting-text">
        {t(label)}
      </AppText>
    </View>
  );
}

// ─── Wizard ───────────────────────────────────────────────────────────────────

export function StepWizard({
  step,
  labels,
  orientation = "horizontal",
  children,
  testID,
}: StepWizardProps) {
  // ─── Vertical layout ──────────────────────────────────────────────────────
  if (orientation === "vertical") {
    return (
      <View testID={testID ?? "step-wizard"} className="flex-row mb-6">
        {/* Left: pills stacked with vertical connectors */}
        <View className="items-start">
          {labels.map((label, i) => {
            const stepNum = i + 1;
            const status: StepStatus =
              stepNum < step ? "completed" : stepNum === step ? "current" : "pending";
            return (
              <React.Fragment key={i}>
                <StepItem label={label} stepNum={stepNum} status={status} />
                {i < labels.length - 1 && (
                  <View
                    testID={`step-connector-${stepNum}`}
                    // ml-[26px] aligns the centre of the 2px line with the circle centre:
                    // border(2) + padding-left(12) + half-circle(12) = 26px
                    className={`w-0.5 h-4 ml-[26px] ${i < step - 1 ? "bg-success" : "bg-stroke-50"}`}
                  />
                )}
              </React.Fragment>
            );
          })}
        </View>
        {/* Right: optional content */}
        {children != null && <View className="flex-1 ml-4">{children}</View>}
      </View>
    );
  }

  // ─── Horizontal layout (default) ──────────────────────────────────────────
  return (
    <View testID={testID ?? "step-wizard"} className="w-full mb-6">
      {/* Pills row with connector lines */}
      <View className="flex-row items-center justify-center w-full  max-w-8xl self-center">
        {labels.map((label, i) => {
          const stepNum = i + 1;
          const status: StepStatus =
            stepNum < step ? "completed" : stepNum === step ? "current" : "pending";
          return (
            <React.Fragment key={i}>
              <StepItem label={label} stepNum={stepNum} status={status} />
              {i < labels.length - 1 && (
                <View
                  testID={`step-connector-${stepNum}`}
                  className={`flex-1 h-0.5 ${i < step - 1 ? "bg-success" : "bg-stroke-50"}`}
                />
              )}
            </React.Fragment>
          );
        })}
      </View>
      {/* Optional content area */}
      {children != null && <View className="mt-4">{children}</View>}
    </View>
  );
}
